import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Work as WorkIcon,
  Home as HomeIcon,
  Coffee as BreakIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { timeTrackingApi } from "../../../services/timetracking-api";
import {
  EmployeeStatus,
  getStatusColor,
  getStatusText,
  formatTime,
} from "../../../types/timetracking";

const TeamStatus: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [allStatuses, setAllStatuses] = useState<EmployeeStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const loadTeamStatuses = async () => {
    try {
      setError(null);
      const statuses = await timeTrackingApi.getAllEmployeeStatuses();
      setAllStatuses(statuses);
      setLastUpdated(new Date());
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("error.failedToLoadTeamStatuses"),
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTeamStatuses();
  };

  // Filter employees based on search and filters
  const filteredStatuses = allStatuses
    .filter((status) => {
      const matchesSearch =
        status.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        status.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        false;

      const matchesDepartment =
        !departmentFilter || status.department === departmentFilter;

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "working" &&
          status.currentStatus === "IN" &&
          !status.isOnBreak) ||
        (statusFilter === "on-break" &&
          status.currentStatus === "IN" &&
          status.isOnBreak) ||
        (statusFilter === "workday-completed" &&
          status.currentStatus === "OUT" &&
          status.isWorkdayCompleted === true) ||
        (statusFilter === "work-hours-incomplete" &&
          status.currentStatus === "OUT" &&
          status.isWorkdayCompleted !== true) ||
        (statusFilter === "not-yet-in" && !status.currentStatus);

      return matchesSearch && matchesDepartment && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by: 1) On Break, 2) Currently working, 3) Workday completed, 4) Work hours incomplete, 5) Not yet in

      // Group 0: On Break (most recent break start first)
      const aOnBreak = a.currentStatus === "IN" && a.isOnBreak;
      const bOnBreak = b.currentStatus === "IN" && b.isOnBreak;

      if (aOnBreak && !bOnBreak) return -1;
      if (!aOnBreak && bOnBreak) return 1;

      if (aOnBreak && bOnBreak) {
        // Both on break - sort by most recent break start
        const aBreakStart = a.currentBreakStart
          ? new Date(a.currentBreakStart).getTime()
          : 0;
        const bBreakStart = b.currentBreakStart
          ? new Date(b.currentBreakStart).getTime()
          : 0;
        return bBreakStart - aBreakStart;
      }

      // Group 1: Currently working (earliest time in first)
      const aWorking = a.currentStatus === "IN" && !a.isOnBreak;
      const bWorking = b.currentStatus === "IN" && !b.isOnBreak;

      if (aWorking && !bWorking) return -1;
      if (!aWorking && bWorking) return 1;

      if (aWorking && bWorking) {
        // Both working - sort by earliest time in
        const aTimeIn = a.lastTimeIn ? new Date(a.lastTimeIn).getTime() : 0;
        const bTimeIn = b.lastTimeIn ? new Date(b.lastTimeIn).getTime() : 0;
        return aTimeIn - bTimeIn;
      }

      // Group 2: Workday completed (earliest time out first)
      const aCompleted =
        a.currentStatus === "OUT" && a.isWorkdayCompleted === true;
      const bCompleted =
        b.currentStatus === "OUT" && b.isWorkdayCompleted === true;

      if (aCompleted && !bCompleted) return -1;
      if (!aCompleted && bCompleted) return 1;

      if (aCompleted && bCompleted) {
        // Both completed - sort by earliest time out
        const aTimeOut = a.lastTimeOut ? new Date(a.lastTimeOut).getTime() : 0;
        const bTimeOut = b.lastTimeOut ? new Date(b.lastTimeOut).getTime() : 0;
        return aTimeOut - bTimeOut;
      }

      // Group 3: Work hours incomplete (earliest time out first)
      const aIncomplete =
        a.currentStatus === "OUT" && a.isWorkdayCompleted !== true;
      const bIncomplete =
        b.currentStatus === "OUT" && b.isWorkdayCompleted !== true;

      if (aIncomplete && !bIncomplete) return -1;
      if (!aIncomplete && bIncomplete) return 1;

      if (aIncomplete && bIncomplete) {
        // Both incomplete - sort by earliest time out
        const aTimeOut = a.lastTimeOut ? new Date(a.lastTimeOut).getTime() : 0;
        const bTimeOut = b.lastTimeOut ? new Date(b.lastTimeOut).getTime() : 0;
        return aTimeOut - bTimeOut;
      }

      // Group 4: Not yet in (sort by employee ID)
      return a.employeeId - b.employeeId;
    });

  // Get unique departments for filter
  const departments = Array.from(
    new Set(allStatuses.map((s) => s.department).filter(Boolean)),
  );

  // Calculate summary statistics
  const onBreakCount = allStatuses.filter(
    (s) => s.currentStatus === "IN" && s.isOnBreak,
  ).length;
  const workingCount = allStatuses.filter(
    (s) => s.currentStatus === "IN" && !s.isOnBreak,
  ).length;
  const workdayCompletedCount = allStatuses.filter(
    (s) => s.currentStatus === "OUT" && s.isWorkdayCompleted === true,
  ).length;
  const workHoursIncompleteCount = allStatuses.filter(
    (s) => s.currentStatus === "OUT" && s.isWorkdayCompleted !== true,
  ).length;
  const notYetInCount = allStatuses.filter((s) => !s.currentStatus).length;

  useEffect(() => {
    loadTeamStatuses();

    // Auto-refresh every 60 seconds
    const interval = setInterval(loadTeamStatuses, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0, pr: 2, pb: 2, pl: 2 }}>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box display="flex" alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "success.main",
                    mr: 1.5,
                    width: 40,
                    height: 40,
                  }}
                >
                  <WorkIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" color="success.main">
                    {workingCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("timetracking.teamStatus.currentlyWorking")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box display="flex" alignItems="center">
                <Avatar
                  sx={{ bgcolor: "info.main", mr: 1.5, width: 40, height: 40 }}
                >
                  <BreakIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" color="info.main">
                    {onBreakCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("timetracking.teamStatus.onBreak")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box display="flex" alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    mr: 1.5,
                    width: 40,
                    height: 40,
                  }}
                >
                  <HomeIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" color="primary.main">
                    {workdayCompletedCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("timetracking.teamStatus.workdayCompleted")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box display="flex" alignItems="center">
                <Avatar
                  sx={{ bgcolor: "error.main", mr: 1.5, width: 40, height: 40 }}
                >
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" color="error.main">
                    {workHoursIncompleteCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("timetracking.teamStatus.workHoursIncomplete")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box display="flex" alignItems="center">
                <Avatar
                  sx={{ bgcolor: "grey.500", mr: 1.5, width: 40, height: 40 }}
                >
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" color="text.secondary">
                    {notYetInCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("timetracking.dashboard.notYetIn")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t("timetracking.teamStatus.searchEmployee")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>
                  {t("timetracking.teamStatus.filterByDepartment")}
                </InputLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  label={t("timetracking.teamStatus.filterByDepartment")}
                >
                  <MenuItem value="">
                    {t("timetracking.filters.allDepartments")}
                  </MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>
                  {t("timetracking.filters.filterByStatus")}
                </InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label={t("timetracking.filters.filterByStatus")}
                >
                  <MenuItem value="">
                    {t("timetracking.filters.allStatuses")}
                  </MenuItem>
                  <MenuItem value="working">
                    {t("timetracking.status.IN")}
                  </MenuItem>
                  <MenuItem value="on-break">
                    {t("timetracking.status.ON_BREAK")}
                  </MenuItem>
                  <MenuItem value="workday-completed">
                    {t("timetracking.status.WORKDAY_COMPLETED")}
                  </MenuItem>
                  <MenuItem value="work-hours-incomplete">
                    {t("timetracking.status.WORK_HOURS_INCOMPLETE")}
                  </MenuItem>
                  <MenuItem value="not-yet-in">
                    {t("timetracking.status.NOT_YET_IN")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadTeamStatuses}>
              {t("common.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Team Status Grid */}
      <Box>
        <Typography variant="h6" component="div" gutterBottom sx={{ mb: 2 }}>
          {t("timetracking.teamStatus.teamMembers")} ({filteredStatuses.length})
        </Typography>

        {filteredStatuses.length === 0 ? (
          <Alert severity="info">
            {t("timetracking.teamStatus.noEmployeesFound")}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {filteredStatuses.map((status) => (
              <Grid
                key={status.employeeId}
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              >
                <Card
                  elevation={1}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      elevation: 4,
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    {/* Header with Avatar and Status */}
                    <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                      <Avatar
                        sx={{
                          bgcolor:
                            getStatusColor(
                              status.currentStatus,
                              status.isWorkdayCompleted,
                              status.isOnBreak,
                            ) === "success"
                              ? "success.main"
                              : getStatusColor(
                                    status.currentStatus,
                                    status.isWorkdayCompleted,
                                    status.isOnBreak,
                                  ) === "primary"
                                ? "primary.main"
                                : getStatusColor(
                                      status.currentStatus,
                                      status.isWorkdayCompleted,
                                      status.isOnBreak,
                                    ) === "error"
                                  ? "error.main"
                                  : getStatusColor(
                                        status.currentStatus,
                                        status.isWorkdayCompleted,
                                        status.isOnBreak,
                                      ) === "info"
                                    ? "info.main"
                                    : "grey.500",
                          width: 48,
                          height: 48,
                        }}
                      >
                        {status.isOnBreak ? <BreakIcon /> : <PersonIcon />}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="body1" fontWeight="bold">
                          {status.employeeName}
                        </Typography>
                        <Chip
                          label={t(
                            getStatusText(
                              status.currentStatus,
                              status.isWorkdayCompleted,
                              status.isOnBreak,
                            ),
                          )}
                          color={
                            getStatusColor(
                              status.currentStatus,
                              status.isWorkdayCompleted,
                              status.isOnBreak,
                            ) as any
                          }
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>

                    {/* Department */}
                    {status.department && (
                      <Box mb={1.5}>
                        <Chip
                          label={status.department}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    )}

                    {/* Session Duration */}
                    <Box mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        {status.isOnBreak
                          ? t("timetracking.labels.breakDuration")
                          : t("timetracking.dashboard.sessionDuration")}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {status.currentSessionDuration}
                      </Typography>
                    </Box>

                    {/* Time Information */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {status.isOnBreak
                          ? t("timetracking.labels.breakStartedAt")
                          : status.currentStatus === "IN"
                            ? t("timetracking.dashboard.workingSince")
                            : t("timetracking.dashboard.lastWorked")}
                      </Typography>
                      {status.lastTimeIn && (
                        <Typography variant="body2" fontWeight="medium">
                          {status.isOnBreak && status.currentBreakStart
                            ? formatTime(status.currentBreakStart)
                            : status.currentStatus === "IN"
                              ? formatTime(status.lastTimeIn)
                              : formatTime(
                                  status.lastTimeOut || status.lastTimeIn,
                                )}
                        </Typography>
                      )}
                      {!status.lastTimeIn && (
                        <Typography variant="body2" color="text.secondary">
                          {t("timetracking.dashboard.neverWorked")}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default TeamStatus;
